import { useMemo, useState, type ChangeEvent } from 'react';
import {
  Clipboard,
  Download,
  FileText,
  History,
  Image,
  Link,
  Save,
  SlidersHorizontal,
  Sparkles,
  X,
} from 'lucide-react';
import type { User } from '@supabase/supabase-js';
import type { Theme } from '../types/theme';
import type { Locale } from '../i18n';
import type { Viewport } from './ViewportSwitcher';
import { PreviewFrame } from './PreviewFrame';
import { ThemeMiniPreview } from './ThemeMiniPreview';
import { appShellColors } from '../styles/appShell';
import { copyTextToClipboard } from '../utils/clipboard';
import {
  createAdvancedExportBundle,
  createThemeFromDesignMd,
  createThemeFromPalette,
  applyThemeAdjustments,
  type CustomThemeDraft,
} from '../utils/themeTools';
import { generateDesignMd, generateToolPrompt } from '../utils/designMdExporter';
import { downloadFile, slugify } from '../utils/download';
import { extractPaletteFromImage } from '../utils/imagePalette';
import {
  saveCustomTheme,
  saveExportHistory,
  saveStyleExtraction,
  type CustomThemeRecord,
  type ExportHistoryRecord,
} from '../utils/designLabData';

type PageType = 'landing' | 'dashboard';
type WorkbenchTab = 'import' | 'extract' | 'customize' | 'history';
type StatusTone = 'idle' | 'success' | 'error';

interface ValueWorkbenchProps {
  customThemes: CustomThemeRecord[];
  exportHistory: ExportHistoryRecord[];
  locale: Locale;
  onClose: () => void;
  onRefreshLibrary: () => Promise<void>;
  pageType: PageType;
  user: User | null;
  viewport: Viewport;
}

const sampleDesignMd = `# Independent Lab Design System

Visual Keywords: independent studio, quiet tool, editorial product
Tone: precise, calm, lightly experimental
Density: medium

| Token | Hex |
| --- | --- |
| Background | #f7f4ef |
| Surface | #ece6dc |
| Surface Elevated | #fffaf2 |
| Text Primary | #201c18 |
| Text Secondary | #675f55 |
| Border | #d8ccbd |
| Primary | #2f6f62 |
| Primary Hover | #285f54 |
| Accent | #9a5b34 |

Heading Font: Inter, system-ui, sans-serif
Body Font: Inter, system-ui, sans-serif
Button Radius: 8px
Card Radius: 12px
Panel Radius: 16px`;

function getCopy(locale: Locale) {
  if (locale === 'zh') {
    return {
      title: '增值功能',
      description: '导入、提取、保存和导出自己的风格。先做可用闭环，不接生成器和支付。',
      signInHint: '登录后可保存自定义主题、提取记录和导出历史。未登录也可以本地试用导入和截图提取。',
      tabs: {
        import: 'DESIGN.md 导入',
        extract: 'URL / 截图提取',
        customize: '自定义主题',
        history: '历史',
      },
      pasteDesignMd: '粘贴 DESIGN.md',
      designMdPlaceholder: '粘贴你的 DESIGN.md，或先用示例内容试一下。',
      useSample: '填入示例',
      importTheme: '生成预览主题',
      saveTheme: '保存主题',
      saveRequiresLogin: '登录后保存',
      saved: '已保存',
      saveFailed: '保存失败',
      generated: '已生成预览主题',
      preview: '完整预览',
      copied: '已复制',
      copyPrompt: '复制 Prompt',
      copyBundle: '复制导出包',
      downloadBundle: '下载导出包',
      primary: '主色',
      radius: '圆角',
      density: '密度',
      densityOptions: {
        low: '低密度',
        medium: '中密度',
        high: '高密度',
      },
      screenshot: '上传截图',
      screenshotHint: '从截图中提取主色与基础 token，适合快速试一个参考视觉方向。',
      url: '网站 URL',
      urlPlaceholder: 'https://example.com',
      extractUrl: '提取 URL',
      extractingUrl: '正在提取...',
      urlHint: '通过同源服务读取公开网页的 HTML / CSS 颜色；如果页面阻止抓取，可改用截图。',
      urlFailed: '无法读取该 URL，请使用截图兜底。',
      noTheme: '还没有生成主题',
      noThemeBody: '先导入 DESIGN.md，或从截图 / URL 提取一个风格。',
      customThemes: '已保存主题',
      noCustomThemes: '还没有保存主题',
      exportHistory: '导出历史',
      noHistory: '还没有导出历史',
      refresh: '刷新',
      close: '关闭增值功能',
    };
  }

  return {
    title: 'Value features',
    description: 'Import, extract, save, and export your own style direction. No generator or payments in this pass.',
    signInHint: 'Sign in to save custom themes, extraction records, and export history. Import and screenshot extraction also work locally.',
    tabs: {
      import: 'DESIGN.md import',
      extract: 'URL / screenshot extraction',
      customize: 'Custom theme',
      history: 'History',
    },
    pasteDesignMd: 'Paste DESIGN.md',
    designMdPlaceholder: 'Paste your DESIGN.md, or try the sample content first.',
    useSample: 'Use sample',
    importTheme: 'Generate preview theme',
    saveTheme: 'Save theme',
    saveRequiresLogin: 'Sign in to save',
    saved: 'Saved',
    saveFailed: 'Save failed',
    generated: 'Preview theme generated',
    preview: 'Full preview',
    copied: 'Copied',
    copyPrompt: 'Copy prompt',
    copyBundle: 'Copy handoff pack',
    downloadBundle: 'Download handoff pack',
    primary: 'Primary',
    radius: 'Radius',
    density: 'Density',
    densityOptions: {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
    },
    screenshot: 'Upload screenshot',
    screenshotHint: 'Extract core colors and basic tokens from a screenshot.',
    url: 'Website URL',
    urlPlaceholder: 'https://example.com',
    extractUrl: 'Extract URL',
    extractingUrl: 'Extracting...',
    urlHint: 'A same-origin service reads colors from public HTML / CSS. Use a screenshot if the page blocks extraction.',
    urlFailed: 'Unable to read that URL. Use a screenshot fallback.',
    noTheme: 'No generated theme yet',
    noThemeBody: 'Import DESIGN.md or extract a style from screenshot / URL first.',
    customThemes: 'Saved themes',
    noCustomThemes: 'No saved themes yet',
    exportHistory: 'Export history',
    noHistory: 'No export history yet',
    refresh: 'Refresh',
    close: 'Close value features',
  };
}

export function ValueWorkbench({
  customThemes,
  exportHistory,
  locale,
  onClose,
  onRefreshLibrary,
  pageType,
  user,
  viewport,
}: ValueWorkbenchProps) {
  const [activeTab, setActiveTab] = useState<WorkbenchTab>('import');
  const [designMdInput, setDesignMdInput] = useState('');
  const [draft, setDraft] = useState<CustomThemeDraft | null>(null);
  const [previewTheme, setPreviewTheme] = useState<Theme | null>(null);
  const [primaryColor, setPrimaryColor] = useState('#2563eb');
  const [radiusScale, setRadiusScale] = useState(1);
  const [density, setDensity] = useState<Theme['mood']['density']>('medium');
  const [urlInput, setUrlInput] = useState('');
  const [isUrlExtracting, setIsUrlExtracting] = useState(false);
  const [status, setStatus] = useState<{ tone: StatusTone; message: string }>({ tone: 'idle', message: '' });
  const copy = getCopy(locale);

  const adjustedTheme = useMemo(() => {
    if (!draft) {
      return null;
    }
    return applyThemeAdjustments(draft.theme, { primary: primaryColor, radiusScale, density });
  }, [density, draft, primaryColor, radiusScale]);

  const currentTheme = adjustedTheme || draft?.theme || null;
  const prompt = currentTheme ? generateToolPrompt(currentTheme, pageType, locale) : '';
  const bundle = currentTheme ? createAdvancedExportBundle(currentTheme, pageType, prompt) : '';

  const handleGenerateFromDesignMd = () => {
    const content = designMdInput.trim();
    if (!content) {
      setDesignMdInput(sampleDesignMd);
      return;
    }

    const nextDraft = createThemeFromDesignMd(content, { sourceKind: 'design_md' });
    setDraft(nextDraft);
    syncControls(nextDraft.theme);
    setActiveTab('customize');
    setStatus({ tone: 'success', message: copy.generated });
  };

  const handleScreenshot = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const palette = await extractPaletteFromImage(file);
      const nextDraft = createThemeFromPalette(palette, {
        name: file.name.replace(/\.[^.]+$/, '') || 'Screenshot Theme',
        sourceKind: 'screenshot',
        sourceLabel: file.name,
      });
      setDraft(nextDraft);
      syncControls(nextDraft.theme);
      setActiveTab('customize');
      setStatus({ tone: 'success', message: copy.generated });
      await saveExtractionIfSignedIn(nextDraft);
    } catch (error) {
      setStatus({ tone: 'error', message: error instanceof Error ? error.message : copy.saveFailed });
    }
  };

  const handleUrlExtraction = async () => {
    const normalizedUrl = urlInput.trim();
    if (!normalizedUrl) {
      return;
    }

    setIsUrlExtracting(true);
    setStatus({ tone: 'idle', message: '' });

    try {
      const response = await fetch(`/api/extract-url?url=${encodeURIComponent(normalizedUrl)}`);
      const result = await response.json() as { colors?: string[]; error?: string; title?: string; url?: string };

      if (!response.ok || !Array.isArray(result.colors) || result.colors.length === 0) {
        throw new Error(result.error || copy.urlFailed);
      }

      const finalUrl = result.url || normalizedUrl;
      const hostname = getHostname(finalUrl) || getHostname(normalizedUrl) || 'Extracted URL';
      const nextDraft = createThemeFromPalette(result.colors, {
        name: result.title || hostname,
        sourceKind: 'url',
        sourceLabel: finalUrl,
        sourceDesignMd: finalUrl,
      });
      setDraft(nextDraft);
      syncControls(nextDraft.theme);
      setActiveTab('customize');
      setStatus({ tone: 'success', message: copy.generated });
      await saveExtractionIfSignedIn(nextDraft);
    } catch {
      setStatus({ tone: 'error', message: copy.urlFailed });
    } finally {
      setIsUrlExtracting(false);
    }
  };

  const handleSaveTheme = async () => {
    if (!user || !currentTheme || !draft) {
      return;
    }

    try {
      await saveCustomTheme({
        userId: user.id,
        name: currentTheme.name,
        sourceKind: draft.sourceKind,
        sourceLabel: draft.sourceLabel,
        theme: currentTheme,
        designMd: generateDesignMd(currentTheme, pageType),
      });
      await onRefreshLibrary();
      setStatus({ tone: 'success', message: copy.saved });
    } catch {
      setStatus({ tone: 'error', message: copy.saveFailed });
    }
  };

  const handleCopyPrompt = async () => {
    if (!currentTheme) {
      return;
    }
    const didCopy = await copyTextToClipboard(prompt);
    if (didCopy && user) {
      await saveExportHistory(user.id, currentTheme, pageType, 'prompt', prompt);
      await onRefreshLibrary();
    }
    setStatus({ tone: didCopy ? 'success' : 'error', message: didCopy ? copy.copied : copy.saveFailed });
  };

  const handleCopyBundle = async () => {
    if (!currentTheme) {
      return;
    }
    const didCopy = await copyTextToClipboard(bundle);
    if (didCopy && user) {
      await saveExportHistory(user.id, currentTheme, pageType, 'bundle', bundle);
      await onRefreshLibrary();
    }
    setStatus({ tone: didCopy ? 'success' : 'error', message: didCopy ? copy.copied : copy.saveFailed });
  };

  const handleDownloadBundle = async () => {
    if (!currentTheme) {
      return;
    }
    downloadFile(bundle, `${slugify(currentTheme.name)}-handoff-pack.md`, 'text/markdown');
    if (user) {
      await saveExportHistory(user.id, currentTheme, pageType, 'bundle', bundle);
      await onRefreshLibrary();
    }
  };

  const syncControls = (theme: Theme) => {
    setPrimaryColor(theme.colors.primary);
    setDensity(theme.mood.density);
    setRadiusScale(parseRadius(theme.radius.card) / 12);
  };

  const saveExtractionIfSignedIn = async (nextDraft: CustomThemeDraft) => {
    if (!user || nextDraft.sourceKind === 'custom') {
      return;
    }

    await saveStyleExtraction({
      userId: user.id,
      sourceKind: nextDraft.sourceKind,
      sourceLabel: nextDraft.sourceLabel,
      theme: nextDraft.theme,
      designMd: nextDraft.designMd,
    });
    await onRefreshLibrary();
  };

  const tabs: Array<{ icon: typeof FileText; key: WorkbenchTab; label: string }> = [
    { icon: FileText, key: 'import', label: copy.tabs.import },
    { icon: Image, key: 'extract', label: copy.tabs.extract },
    { icon: SlidersHorizontal, key: 'customize', label: copy.tabs.customize },
    { icon: History, key: 'history', label: copy.tabs.history },
  ];

  if (previewTheme) {
    return (
      <PreviewFrame
        theme={previewTheme}
        themes={currentTheme ? [currentTheme] : [previewTheme]}
        onClose={() => setPreviewTheme(null)}
        pageType={pageType}
        viewport={viewport}
        locale={locale}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/30">
      <section
        className="ml-auto flex h-full w-full max-w-5xl flex-col border-l shadow-2xl"
        style={{ backgroundColor: appShellColors.surface, borderColor: appShellColors.border }}
        aria-label={copy.title}
      >
        <header className="border-b p-4" style={{ borderColor: appShellColors.border }}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold" style={{ color: appShellColors.textPrimary }}>
                {copy.title}
              </h2>
              <p className="mt-1 max-w-3xl text-sm" style={{ color: appShellColors.textSecondary }}>
                {copy.description}
              </p>
              <p className="mt-2 text-xs" style={{ color: appShellColors.textMuted }}>
                {copy.signInHint}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={copy.close}
              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{ color: appShellColors.textPrimary, ['--tw-ring-color' as string]: appShellColors.focusRing }}
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>

          <nav className="mt-4 flex flex-wrap gap-2" aria-label={copy.title}>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  aria-pressed={isActive}
                  className="inline-flex min-h-11 items-center gap-2 rounded-lg border px-3 text-sm font-medium"
                  style={{
                    backgroundColor: isActive ? appShellColors.primary : appShellColors.surface,
                    borderColor: isActive ? appShellColors.primary : appShellColors.border,
                    color: isActive ? appShellColors.onPrimary : appShellColors.textSecondary,
                  }}
                >
                  <Icon size={16} aria-hidden="true" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </header>

        <main className="grid min-h-0 flex-1 gap-0 overflow-hidden lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="overflow-auto p-4">
            {activeTab === 'import' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <label className="text-sm font-medium" style={{ color: appShellColors.textPrimary }} htmlFor="design-md-import">
                    {copy.pasteDesignMd}
                  </label>
                  <button
                    type="button"
                    onClick={() => setDesignMdInput(sampleDesignMd)}
                    className="min-h-10 rounded-lg border px-3 text-sm font-medium"
                    style={{ borderColor: appShellColors.border, color: appShellColors.textSecondary }}
                  >
                    {copy.useSample}
                  </button>
                </div>
                <textarea
                  id="design-md-import"
                  value={designMdInput}
                  onChange={(event) => setDesignMdInput(event.target.value)}
                  placeholder={copy.designMdPlaceholder}
                  className="min-h-[360px] w-full rounded-lg border p-3 font-mono text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  style={{
                    backgroundColor: appShellColors.background,
                    borderColor: appShellColors.border,
                    color: appShellColors.textPrimary,
                    ['--tw-ring-color' as string]: appShellColors.focusRing,
                  }}
                />
                <button
                  type="button"
                  onClick={handleGenerateFromDesignMd}
                  className="inline-flex min-h-11 items-center gap-2 rounded-lg px-4 text-sm font-medium"
                  style={{ backgroundColor: appShellColors.primary, color: appShellColors.onPrimary }}
                >
                  <Sparkles size={16} aria-hidden="true" />
                  {copy.importTheme}
                </button>
              </div>
            )}

            {activeTab === 'extract' && (
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-lg border p-4" style={{ borderColor: appShellColors.border }}>
                  <label className="flex min-h-36 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed p-6 text-center" style={{ borderColor: appShellColors.border }}>
                    <Image size={24} aria-hidden="true" style={{ color: appShellColors.textMuted }} />
                    <span className="mt-3 text-sm font-medium" style={{ color: appShellColors.textPrimary }}>
                      {copy.screenshot}
                    </span>
                    <span className="mt-2 text-xs" style={{ color: appShellColors.textMuted }}>
                      {copy.screenshotHint}
                    </span>
                    <input className="sr-only" type="file" accept="image/png,image/jpeg,image/webp" onChange={handleScreenshot} />
                  </label>
                </div>

                <div className="rounded-lg border p-4" style={{ borderColor: appShellColors.border }}>
                  <label className="text-sm font-medium" style={{ color: appShellColors.textPrimary }} htmlFor="url-extract">
                    {copy.url}
                  </label>
                  <div className="mt-3 flex gap-2">
                    <input
                      id="url-extract"
                      type="url"
                      value={urlInput}
                      onChange={(event) => setUrlInput(event.target.value)}
                      placeholder={copy.urlPlaceholder}
                      className="min-h-11 min-w-0 flex-1 rounded-lg border px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                      style={{
                        backgroundColor: appShellColors.background,
                        borderColor: appShellColors.border,
                        color: appShellColors.textPrimary,
                        ['--tw-ring-color' as string]: appShellColors.focusRing,
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleUrlExtraction}
                      disabled={isUrlExtracting}
                      className="inline-flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium"
                      style={{ backgroundColor: appShellColors.primary, color: appShellColors.onPrimary }}
                    >
                      <Link size={16} aria-hidden="true" />
                      {isUrlExtracting ? copy.extractingUrl : copy.extractUrl}
                    </button>
                  </div>
                  <p className="mt-3 text-xs" style={{ color: appShellColors.textMuted }}>
                    {copy.urlHint}
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'customize' && (
              <div className="space-y-5">
                {currentTheme ? (
                  <>
                    <div className="grid gap-4 md:grid-cols-3">
                      <label className="rounded-lg border p-4" style={{ borderColor: appShellColors.border }}>
                        <span className="text-sm font-medium" style={{ color: appShellColors.textPrimary }}>
                          {copy.primary}
                        </span>
                        <input
                          type="color"
                          value={primaryColor}
                          onChange={(event) => setPrimaryColor(event.target.value)}
                          className="mt-3 h-11 w-full rounded-lg border"
                          style={{ borderColor: appShellColors.border }}
                        />
                      </label>

                      <label className="rounded-lg border p-4" style={{ borderColor: appShellColors.border }}>
                        <span className="text-sm font-medium" style={{ color: appShellColors.textPrimary }}>
                          {copy.radius}
                        </span>
                        <input
                          type="range"
                          min="0.5"
                          max="2"
                          step="0.1"
                          value={radiusScale}
                          onChange={(event) => setRadiusScale(Number(event.target.value))}
                          className="mt-5 w-full"
                        />
                      </label>

                      <label className="rounded-lg border p-4" style={{ borderColor: appShellColors.border }}>
                        <span className="text-sm font-medium" style={{ color: appShellColors.textPrimary }}>
                          {copy.density}
                        </span>
                        <select
                          value={density}
                          onChange={(event) => setDensity(event.target.value as Theme['mood']['density'])}
                          className="mt-3 min-h-11 w-full rounded-lg border px-3 text-sm"
                          style={{
                            backgroundColor: appShellColors.background,
                            borderColor: appShellColors.border,
                            color: appShellColors.textPrimary,
                          }}
                        >
                          <option value="low">{copy.densityOptions.low}</option>
                          <option value="medium">{copy.densityOptions.medium}</option>
                          <option value="high">{copy.densityOptions.high}</option>
                        </select>
                      </label>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setPreviewTheme(currentTheme)}
                        className="inline-flex min-h-11 items-center gap-2 rounded-lg border px-3 text-sm font-medium"
                        style={{ borderColor: appShellColors.border, color: appShellColors.textPrimary }}
                      >
                        <Sparkles size={16} aria-hidden="true" />
                        {copy.preview}
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveTheme}
                        disabled={!user}
                        className="inline-flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-medium disabled:opacity-45"
                        style={{ backgroundColor: appShellColors.primary, color: appShellColors.onPrimary }}
                      >
                        <Save size={16} aria-hidden="true" />
                        {user ? copy.saveTheme : copy.saveRequiresLogin}
                      </button>
                      <button
                        type="button"
                        onClick={handleCopyPrompt}
                        className="inline-flex min-h-11 items-center gap-2 rounded-lg border px-3 text-sm font-medium"
                        style={{ borderColor: appShellColors.border, color: appShellColors.textPrimary }}
                      >
                        <Clipboard size={16} aria-hidden="true" />
                        {copy.copyPrompt}
                      </button>
                      <button
                        type="button"
                        onClick={handleCopyBundle}
                        className="inline-flex min-h-11 items-center gap-2 rounded-lg border px-3 text-sm font-medium"
                        style={{ borderColor: appShellColors.border, color: appShellColors.textPrimary }}
                      >
                        <Clipboard size={16} aria-hidden="true" />
                        {copy.copyBundle}
                      </button>
                      <button
                        type="button"
                        onClick={handleDownloadBundle}
                        className="inline-flex min-h-11 items-center gap-2 rounded-lg border px-3 text-sm font-medium"
                        style={{ borderColor: appShellColors.border, color: appShellColors.textPrimary }}
                      >
                        <Download size={16} aria-hidden="true" />
                        {copy.downloadBundle}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="rounded-lg border p-8 text-center" style={{ borderColor: appShellColors.border }}>
                    <h3 className="font-semibold" style={{ color: appShellColors.textPrimary }}>
                      {copy.noTheme}
                    </h3>
                    <p className="mt-2 text-sm" style={{ color: appShellColors.textMuted }}>
                      {copy.noThemeBody}
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div className="grid gap-5 lg:grid-cols-2">
                <HistoryList copy={copy} records={exportHistory} />
                <CustomThemeList
                  copy={copy}
                  records={customThemes}
                  onSelect={(theme) => {
                    const nextDraft: CustomThemeDraft = {
                      designMd: generateDesignMd(theme, pageType),
                      sourceKind: 'custom',
                      sourceLabel: theme.name,
                      theme,
                    };
                    setDraft(nextDraft);
                    syncControls(theme);
                    setActiveTab('customize');
                  }}
                />
              </div>
            )}

            {status.tone !== 'idle' && (
              <p
                className="mt-4 rounded-lg border px-3 py-2 text-sm"
                style={{
                  backgroundColor: status.tone === 'success' ? '#ecfdf5' : '#fef2f2',
                  borderColor: status.tone === 'success' ? '#bbf7d0' : '#fecaca',
                  color: status.tone === 'success' ? '#166534' : '#991b1b',
                }}
                role="status"
              >
                {status.message}
              </p>
            )}
          </div>

          <aside className="min-h-0 overflow-auto border-t p-4 lg:border-l lg:border-t-0" style={{ borderColor: appShellColors.border }}>
            {currentTheme ? (
              <div className="space-y-4">
                <ThemeMiniPreview theme={currentTheme} pageType={pageType} viewport={viewport} />
                <div>
                  <h3 className="font-semibold" style={{ color: appShellColors.textPrimary }}>
                    {currentTheme.name}
                  </h3>
                  <p className="mt-1 text-sm" style={{ color: appShellColors.textMuted }}>
                    {currentTheme.description}
                  </p>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {Object.values(currentTheme.colors).slice(0, 10).map((color, index) => (
                    <div
                      key={`${color}-${index}`}
                      className="h-9 rounded-lg border"
                      style={{ backgroundColor: color, borderColor: appShellColors.border }}
                      title={color}
                    />
                  ))}
                </div>
                <pre
                  className="max-h-80 overflow-auto rounded-lg border p-3 text-xs"
                  style={{ backgroundColor: appShellColors.background, borderColor: appShellColors.border, color: appShellColors.textSecondary }}
                >
                  {JSON.stringify(currentTheme, null, 2)}
                </pre>
              </div>
            ) : (
              <div className="rounded-lg border p-6 text-center" style={{ borderColor: appShellColors.border }}>
                <h3 className="font-semibold" style={{ color: appShellColors.textPrimary }}>
                  {copy.noTheme}
                </h3>
                <p className="mt-2 text-sm" style={{ color: appShellColors.textMuted }}>
                  {copy.noThemeBody}
                </p>
              </div>
            )}
          </aside>
        </main>
      </section>
    </div>
  );
}

function HistoryList({ copy, records }: { copy: ReturnType<typeof getCopy>; records: ExportHistoryRecord[] }) {
  return (
    <section>
      <h3 className="font-semibold" style={{ color: appShellColors.textPrimary }}>
        {copy.exportHistory}
      </h3>
      <div className="mt-3 space-y-2">
        {records.length === 0 ? (
          <EmptyText text={copy.noHistory} />
        ) : records.map((record) => (
          <article key={record.id} className="rounded-lg border p-3" style={{ borderColor: appShellColors.border }}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h4 className="truncate text-sm font-medium" style={{ color: appShellColors.textPrimary }}>
                  {record.theme_name}
                </h4>
                <p className="text-xs" style={{ color: appShellColors.textMuted }}>
                  {record.export_kind} · {new Date(record.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function CustomThemeList({
  copy,
  onSelect,
  records,
}: {
  copy: ReturnType<typeof getCopy>;
  onSelect: (theme: Theme) => void;
  records: CustomThemeRecord[];
}) {
  return (
    <section>
      <h3 className="font-semibold" style={{ color: appShellColors.textPrimary }}>
        {copy.customThemes}
      </h3>
      <div className="mt-3 space-y-2">
        {records.length === 0 ? (
          <EmptyText text={copy.noCustomThemes} />
        ) : records.map((record) => (
          <button
            key={record.id}
            type="button"
            onClick={() => onSelect(record.theme)}
            className="block w-full rounded-lg border p-3 text-left"
            style={{ borderColor: appShellColors.border }}
          >
            <h4 className="truncate text-sm font-medium" style={{ color: appShellColors.textPrimary }}>
              {record.name}
            </h4>
            <p className="text-xs" style={{ color: appShellColors.textMuted }}>
              {record.source_kind} · {new Date(record.created_at).toLocaleString()}
            </p>
          </button>
        ))}
      </div>
    </section>
  );
}

function EmptyText({ text }: { text: string }) {
  return (
    <div className="rounded-lg border p-4 text-sm" style={{ borderColor: appShellColors.border, color: appShellColors.textMuted }}>
      {text}
    </div>
  );
}

function parseRadius(radius: string): number {
  const match = radius.match(/\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : 12;
}

function getHostname(value: string): string {
  const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;

  try {
    return new URL(withProtocol).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}
